import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { StatsSection } from 'widgets/statspro/js/components/common/stats-section';

import {
    CompNavItem,
    ResponsiveRoundNavItem,
    ResponsiveSeasonNavItem,
    RoundSelectNavItem
} from 'common/react/components/competition-nav/nav-items';
import { navSelector } from 'common/store/modules/competition-nav/selectors';
import { useCompetitionNavigationOptions } from 'common/react/hooks/use-competition-navigation-options';
import PropTypes from 'prop-types';
import { ALLOWED_COMPS } from 'widgets/ladder-v2/js/components/LadderNav';
import { FilterDropdown } from 'widgets/filters/js/components/FilterDropdown';
import { getTranslation } from 'common/react/utils/translations';
import { FilterButton } from 'common/react/components/FilterButton';
import { useWindowWidth } from 'common/react/hooks/use-window-resize';
import { StatsButton } from 'common/react/components/StatsButton';

import { initialiseNav } from 'common/store/modules/competition-nav/thunks';
import { useGetUrlParams } from 'common/react/components/competition-nav/hooks/use-get-url-params';
import { useSyncUrlParams } from 'common/react/hooks/use-sync-url-params';

const TIMEOUT = 500;

export const CustomLadderNav = ({ seasonId, competitionId }) => {
    const dispatch = useDispatch();

    const params = useGetUrlParams();

    const initialValues = {
        competitionId: params.competition || competitionId,
        seasonId: params.season || seasonId,
        roundId: params.round || ''
    };

    const nav = useSelector(navSelector);
    const options = useCompetitionNavigationOptions(nav);
    const competitionNavRef = useRef();
    const windowWidth = useWindowWidth();
    const isResponsive = () =>
        typeof PULSE?.app?.measurements?.desktop === 'number' &&
        windowWidth <= PULSE.app.measurements.desktop;
    const [showResponsiveItems, setShowResponsiveItems] = useState(false);

    const [areFilterOptionsVisible, setAreFilterOptionsVisible] =
        useState(false);
    const [resetFilterBtnStyles, setResetFilterBtnStyles] = useState(false);

    useEffect(() => {
        setShowResponsiveItems(isResponsive());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [windowWidth]);

    let timeoutId = null;

    useEffect(() => {
        dispatch(
            initialiseNav({ initialValues: initialValues, removeFinals: true })
        );
    }, [dispatch]);

    useSyncUrlParams({
        selector: navSelector,
        mapToParams: (items) => ({
            Competition: items.competition.id,
            Season: items.season.id,
            Round: items.round.id
        })
    });

    const { competitions, seasons } = options;
    const season = seasons?.find((item) => item.id === nav.season.id);

    useEffect(() => {
        // clear prev timeout
        if (timeoutId) {
            clearTimeout(timeoutId);
        }

        timeoutId = setTimeout(() => {
            if (nav.ready) {
                dispatchLadderInit();
            }
        }, TIMEOUT);

        return () => {
            clearTimeout(timeoutId);
        };
    }, [nav]);

    const dispatchLadderInit = () => {
        const initParams = {
            roundId: nav.round.id
        };

        const string = Object.keys(initParams)
            .map((key) => {
                return (
                    encodeURIComponent(key) +
                    '=' +
                    encodeURIComponent(initParams[key])
                );
            })
            .join('&');

        const currentRoundNumber =
            season?.currentRoundNumber === 0 ? 1 : season?.currentRoundNumber;

        const isFutureRound = nav.round.roundNumber > currentRoundNumber;

        const endpoint = `${PULSE.app.environment.api}compseasons/${
            nav.season.id
        }/ladders${nav.round?.id && !isFutureRound ? '?' + string : ''}`;

        dispatch(
            PULSE.app.redux.actions.customtable.RE_INIT_TABLE.request({
                tableName: 'ladder',
                apiUrl: endpoint,
                retryEndPoint: false
            })
        );
    };

    const hasRoundChanged = useCallback(() => {
        if (!nav?.round) {
            return false;
        }

        return nav?.round?.id !== nav?.round?.initialItemId;
    }, [nav?.round]);

    const isSeasonChanged = useCallback(() => {
        if (!nav?.season) {
            return false;
        }

        return (
            Number.parseInt(nav?.season?.id, 10) !==
            Number.parseInt(seasonId, 10)
        );
    }, [nav?.season]);

    /**
     * Checks if any navigation filters have changed from their initial default values.
     *
     * @returns {boolean} True if any filter has changed from defaults
     */
    const hasChangedFromDefaults = () => {
        if (!nav?.competition || !nav?.season || !nav?.round) {
            return false;
        }
        const isCompetitionChanged =
            nav?.competition?.id !== nav?.competition?.initialItemId;

        return isCompetitionChanged || isSeasonChanged() || hasRoundChanged();
    };

    /**
     * Resets all navigation filters (competition, season, round) back to their initial default values.
     */
    const resetToDefaults = () => {
        const initialCompetitionId = competitionId || '';
        const initialSeasonId = Number.parseInt(seasonId) || '';
        const initialRoundId = nav?.round?.initialItemId || '';

        if (
            nav?.competition?.id !== initialCompetitionId ||
            nav?.season?.id !== initialSeasonId ||
            nav?.round?.id !== initialRoundId
        ) {
            dispatch(
                initialiseNav({
                    initialValues: {
                        competitionId,
                        seasonId,
                        roundId: nav?.round?.initialItemId || ''
                    }
                })
            );
        }
    };

    const renderMobile = () => {
        return (
            <>
                <StatsSection.NavItems>
                    <CompNavItem
                        competitions={competitions}
                        allowListOrder={ALLOWED_COMPS}
                        hideLabel={true}
                        classes="fixtures-nav__comp-select"
                        removeFinals
                    />
                    <FilterButton
                        classes="fixtures-nav__filter-button"
                        onChange={() =>
                            setAreFilterOptionsVisible((prev) => !prev)
                        }
                        areFilterOptionsVisible={areFilterOptionsVisible}
                        resetFilterBtnStyles={resetFilterBtnStyles}
                        setResetFilterBtnStyles={setResetFilterBtnStyles}
                        showFilterLabel={false}
                        showActiveCircle
                        isFilterSelectedCustom={() =>
                            isSeasonChanged() || hasRoundChanged()
                        }
                    />
                </StatsSection.NavItems>
                {areFilterOptionsVisible && (
                    <StatsSection.NavFilterItems
                        hideReset
                        onReset={resetToDefaults}
                        initialValues={{
                            competitionId,
                            seasonId
                        }}
                    >
                        <ResponsiveSeasonNavItem
                            seasons={seasons}
                            type="with-default"
                            removeFinals
                        />
                        <ResponsiveRoundNavItem
                            removeFinals
                            selector={navSelector}
                            hideLabel
                        />
                        {hasChangedFromDefaults() ? (
                            <div className="stats-leaders-nav__item-wrapper stats-leaders-nav__item-wrapper--reset">
                                <button
                                    className="stats-leaders-filter-reset-button"
                                    onClick={resetToDefaults}
                                >
                                    {getTranslation(
                                        'label.statsLeaders.filters.resetButtonText'
                                    )}
                                </button>
                            </div>
                        ) : null}
                    </StatsSection.NavFilterItems>
                )}
                <StatsSection.SettingsButton classes="fixtures-nav__settings-button">
                    <StatsButton />
                </StatsSection.SettingsButton>
            </>
        );
    };

    const renderDesktop = () => {
        return (
            <>
                <StatsSection.NavItems>
                    <CompNavItem
                        competitions={competitions}
                        allowListOrder={ALLOWED_COMPS}
                        hideLabel={false}
                    />
                    {!showResponsiveItems ? (
                        <FilterDropdown
                            title="Season"
                            icon="season-trophy"
                            navItem="season"
                            items={seasons}
                            removeFinals
                            onChange={() =>
                                setAreFilterOptionsVisible((prev) => !prev)
                            }
                        />
                    ) : null}

                    {!showResponsiveItems ? (
                        <RoundSelectNavItem removeFinals={true} />
                    ) : null}
                    {hasChangedFromDefaults() ? (
                        <div className="stats-leaders-nav__item-wrapper stats-leaders-nav__item-wrapper--reset">
                            <button
                                className="stats-leaders-filter-reset-button"
                                onClick={resetToDefaults}
                            >
                                {getTranslation(
                                    'label.statsLeaders.filters.resetButtonText'
                                )}
                            </button>
                        </div>
                    ) : null}
                </StatsSection.NavItems>

                {showResponsiveItems ? (
                    <StatsSection.NavFilterItems hideReset={true}>
                        <ResponsiveSeasonNavItem
                            seasons={seasons}
                            removeFinals
                        />
                        <ResponsiveRoundNavItem
                            removeFinals
                            selector={navSelector}
                            hideLabel={true}
                        />
                    </StatsSection.NavFilterItems>
                ) : null}

                <StatsSection.SettingsButton>
                    <StatsButton />
                </StatsSection.SettingsButton>
            </>
        );
    };

    return (
        <div
            className="competition-nav filter-dropdown rounded-filter-wrapper"
            ref={competitionNavRef}
        >
            <StatsSection.Nav
                showResponsiveItems={showResponsiveItems}
                showFilterItems={areFilterOptionsVisible}
                withScrollableFilter={!showResponsiveItems}
            >
                {showResponsiveItems ? renderMobile() : renderDesktop()}
            </StatsSection.Nav>
        </div>
    );
};

CustomLadderNav.propTypes = {
    seasonId: PropTypes.string,
    competitionId: PropTypes.string
};
